import { memo } from 'react'
import { useNavigate } from 'react-router-dom'

import { ProfileAlert } from '@modules/alerts/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import ContactAttendeesPopover from '@modules/chat/components/ContactAttendeesPopover'
import { CompanyContactsMap } from '@modules/contacts/types'
import { GroupedResultProfile, ProfileType } from '@modules/search/types'

import { PREVIEW_FIELDS_MAP_TO_PROFILES } from '../constants'
import { getCompanyId, getProfileTitle } from '../util/ResultProfile.util'
import ResultProfileItem from './ResultProfileItem'
import { useContactHandler } from './ResultProfileList'
import { ResultCardGroupSubItemContainer, ResultCardItemContainer } from './ResultProfileList.styles'

export interface GroupedResultProfileListProps {
  groups: GroupedResultProfile[]
  companyContactsMap?: CompanyContactsMap
  profilesAlertsMap?: Record<string, ProfileAlert>
  contactableMap?: Record<string, boolean>
  setScrollX?: (page: number) => void
  setScrollY?: (page: number) => void
}

const GroupedResultProfileList = ({
  groups,
  companyContactsMap = {},
  profilesAlertsMap,
  contactableMap,
  setScrollX,
  setScrollY,
}: GroupedResultProfileListProps) => {
  const { user } = useAuthContext()

  const navigate = useNavigate()

  const { contactAttendeesPopoverData, getContactHandler, setContactAttendeesPopoverData, openChatModal } = useContactHandler(
    groups.flatMap(group => [...group.profiles, group.company]),
  )

  if (!groups?.length) {
    return null
  }

  return (
    // eslint-disable-next-line
    <>
      {groups?.map(group => {
        /**
         * it is technically must not be possible to but because of some strange
         * reason it still renders after back button pressed even if selectedGroupingId
         * has not been ever touched.
         * */
        if (!group.company) {
          return null
        }

        return (
          // eslint-disable-next-line
          <div key={group.company.id}>
            <ResultCardItemContainer key={group.company.id} data-testid={`grouped-profiles-company-${group.company.id}`}>
              <ResultProfileItem
                fields={PREVIEW_FIELDS_MAP_TO_PROFILES.company}
                resultProfile={group.company}
                renderTitle={() => getProfileTitle(group.company)}
                isContactButtonEnabled={group.company.id !== user?.companyId && contactableMap?.[group.company.id]}
                onReadMoreClick={() => {
                  if (setScrollX) {
                    setScrollX(window.scrollX)
                  }
                  if (setScrollY) {
                    setScrollY(window.scrollY)
                  }

                  setTimeout(() => {
                    navigate(`/plus/profile/${group.company.id}/company`)
                  }, 100)
                }}
                onContactClick={getContactHandler(group.company)}
                companyContactCounts={companyContactsMap[group.company.id]?.counts}
                recentMeeting={companyContactsMap[group.company.id]?.recentMeeting}
                fieldsGrid={{
                  lg: [2, 3, 3],
                  md: [2, 5, 5],
                }}
                profileAlert={profilesAlertsMap?.[group.company.id]}
              />
            </ResultCardItemContainer>
            {group.profiles?.map(profile => (
              <ResultCardGroupSubItemContainer key={profile.id} data-testid={`grouped-profiles-company-profile-${profile.id}`}>
                <ResultProfileItem
                  isViewed={profile._isViewed ?? true}
                  fields={PREVIEW_FIELDS_MAP_TO_PROFILES[profile['@type']]}
                  resultProfile={profile}
                  isContactButtonEnabled={
                    (profile.id !== user?.id && profile['@type'] === ProfileType.delegate) ||
                    contactableMap?.[getCompanyId(profile)]
                  }
                  renderTitle={() => getProfileTitle(profile)}
                  onReadMoreClick={() => {
                    if (setScrollX) {
                      setScrollX(window.scrollX)
                    }
                    if (setScrollY) {
                      setScrollY(window.scrollY)
                    }

                    setTimeout(() => {
                      navigate(`/plus/profile/${profile.id}/${profile['@type']}`)
                    }, 100)
                  }}
                  onContactClick={getContactHandler(profile)}
                  fieldsGrid={{
                    lg: [2, 3, 3],
                    md: [2, 5, 5],
                  }}
                  profileAlert={profilesAlertsMap?.[profile.id]}
                />
              </ResultCardGroupSubItemContainer>
            ))}
          </div>
        )
      })}
      {!!contactAttendeesPopoverData?.profile && (
        <ContactAttendeesPopover
          anchor={contactAttendeesPopoverData?.anchor}
          profile={contactAttendeesPopoverData?.profile}
          close={() => setContactAttendeesPopoverData(null)}
          openChatModal={openChatModal}
        />
      )}
    </>
  )
}

export default memo(GroupedResultProfileList)
